import React, { createContext, useContext, useState } from "react";
import PortalAuthService from "../services/PortalAuthService";

export const PortalAuthContext = createContext();

export const usePortalAuth = () => useContext(PortalAuthContext);

const readMemberships = () => {
    try {
        return JSON.parse(localStorage.getItem("PORTAL_MEMBERSHIPS")) || [];
    } catch {
        return [];
    }
};

export const PortalAuthProvider = ({ children }) => {
    const [portalToken, setPortalToken] = useState(
        () => localStorage.getItem("PORTAL_TOKEN") || null
    );
    const [customerName, setCustomerName] = useState(
        () => localStorage.getItem("PORTAL_CUSTOMER_NAME") || null
    );
    const [clientName, setClientName] = useState(
        () => localStorage.getItem("PORTAL_CLIENT_NAME") || null
    );
    const [clientLogoBase64, setClientLogoBase64] = useState(
        () => localStorage.getItem("PORTAL_CLIENT_LOGO") || null
    );
    const [memberships, setMemberships] = useState(readMemberships);

    // Used by PortalAuthGuard's /me refresh — a session opened before this
    // build (or one where the picker was dismissed) never got memberships any
    // other way, since only login()/switchCompany() used to populate them.
    const setMembershipsPersisted = (members) => {
        localStorage.setItem("PORTAL_MEMBERSHIPS", JSON.stringify(members || []));
        setMemberships(members || []);
    };

    // Login and company-switch responses share the same shape.
    const applySession = (data) => {
        const token = data.token || data.jwtToken;
        const customer = data.customerName || data.customer?.name || null;
        const client = data.clientName || data.client?.name || null;
        const logo = data.clientLogoBase64 || null;
        const members = data.memberships || [];

        localStorage.setItem("PORTAL_TOKEN", token);
        if (customer) localStorage.setItem("PORTAL_CUSTOMER_NAME", customer);
        if (client) localStorage.setItem("PORTAL_CLIENT_NAME", client);
        if (logo) localStorage.setItem("PORTAL_CLIENT_LOGO", logo);
        else localStorage.removeItem("PORTAL_CLIENT_LOGO");
        localStorage.setItem("PORTAL_MEMBERSHIPS", JSON.stringify(members));

        setPortalToken(token);
        setCustomerName(customer);
        setClientName(client);
        setClientLogoBase64(logo);
        setMemberships(members);
    };

    const login = async (credentials) => {
        const data = await PortalAuthService.login(credentials);
        applySession(data);
        return data;
    };

    // Mints a token for another membership and applies it. Callers are expected
    // to reload/navigate afterwards so page data refetches under the new scope.
    const switchCompany = async (customerId) => {
        const data = await PortalAuthService.switchCompany(customerId);
        applySession(data);
        return data;
    };

    const logout = () => {
        localStorage.removeItem("PORTAL_TOKEN");
        localStorage.removeItem("PORTAL_CUSTOMER_NAME");
        localStorage.removeItem("PORTAL_CLIENT_NAME");
        localStorage.removeItem("PORTAL_CLIENT_LOGO");
        localStorage.removeItem("PORTAL_MEMBERSHIPS");

        setPortalToken(null);
        setCustomerName(null);
        setClientName(null);
        setClientLogoBase64(null);
        setMemberships([]);

        window.location.href = "/portal/login";
    };

    return (
        <PortalAuthContext.Provider value={{ portalToken, customerName, clientName, clientLogoBase64, memberships, setClientLogoBase64, setClientName, setCustomerName, setMemberships: setMembershipsPersisted, login, switchCompany, logout }}>
            {children}
        </PortalAuthContext.Provider>
    );
};
